import { Card, CardContent, CardHeader } from '../../components/ui/Card';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleInfo,
  faChartLine,
  faBook,
  faCheckCircle,
  faExclamationTriangle,
  faLanguage,
  faFileLines,
  faBalanceScale,
  faGraduationCap,
  faFlask,
  faExternalLinkAlt,
  faDownload
} from '@fortawesome/free-solid-svg-icons';
import { API_URL } from '../../config/api';

export default function HelpPage() {
  const handleDownloadCorpus = () => {
    window.open(`${API_URL}/corpus/download-public`, '_blank');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-8 shadow-lg">
        <div className="flex items-center gap-4">
          <FontAwesomeIcon icon={faCircleInfo} className="text-5xl text-white" />
          <div>
            <h1 className="text-3xl font-bold mb-2 text-white">Centro de Ayuda</h1>
            <p className="text-white/90">
              Guía completa sobre cómo utilizar la herramienta de análisis de Lenguaje Claro
            </p>
          </div>
        </div>
      </div>

      {/* Qué es Lenguaje Claro */}
      <Card>
        <CardHeader>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            <FontAwesomeIcon icon={faBook} className="text-indigo-600" />
            ¿Qué es Lenguaje Claro?
          </h2>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-gray-700 leading-relaxed">
            El <strong>Lenguaje Claro</strong> es una forma de redacción que facilita la comprensión de los textos,
            especialmente de documentos administrativos, legales y técnicos, haciéndolos accesibles para todas las personas.
          </p>
          <p className="text-gray-700 leading-relaxed">
            Esta herramienta analiza la <strong>comprensibilidad</strong> de tus textos según criterios objetivos basados
            en normativas internacionales y evalúa el nivel de comprensión según el <strong>Marco Común Europeo de Referencia
            para las Lenguas (MCERL)</strong>, específicamente para alcanzar el nivel <strong>B2</strong>, que garantiza
            que el contenido sea comprensible para la mayoría de la población.
          </p>
        </CardContent>
      </Card>

      {/* Marco Normativo */}
      <Card>
        <CardHeader>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            <FontAwesomeIcon icon={faBalanceScale} className="text-blue-600" />
            Marco Normativo de Lenguaje Claro
          </h2>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-gray-700 leading-relaxed">
            El análisis de comprensibilidad de esta herramienta se fundamenta en las siguientes normativas:
          </p>

          <div className="space-y-4 mt-4">
            {/* UNE-ISO 24495-1:2024 */}
            <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-lg p-4">
              <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                <span className="text-blue-600">📋</span>
                UNE-ISO 24495-1:2024 - Lenguaje Claro
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Norma internacional que establece los principios y directrices para la redacción en lenguaje claro,
                proporcionando criterios objetivos para evaluar la claridad y comprensibilidad de documentos escritos.
              </p>
            </div>

            {/* Acta Europea de Accesibilidad */}
            <div className="bg-purple-50 border-l-4 border-purple-500 rounded-r-lg p-4">
              <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                <span className="text-purple-600">🇪🇺</span>
                Acta Europea de Accesibilidad
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Directiva europea que establece requisitos de accesibilidad para productos y servicios,
                incluyendo la obligación de proporcionar información clara y comprensible.
              </p>
            </div>

            {/* Ley 11/2023 */}
            <div className="bg-green-50 border-l-4 border-green-500 rounded-r-lg p-4">
              <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                <span className="text-green-600">⚖️</span>
                Ley 11/2023 de Accesibilidad
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Ley española que transpone el Acta Europea de Accesibilidad, estableciendo la obligatoriedad
                de garantizar la accesibilidad cognitiva y el uso de lenguaje claro en la comunicación pública y privada.
              </p>
            </div>
          </div>

          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mt-6">
            <h3 className="font-bold text-gray-800 mb-3">Principios Fundamentales del Lenguaje Claro</h3>
            <p className="text-gray-700 text-sm mb-3">
              Siguiendo las directrices de estas normativas, esta herramienta evalúa cuatro principios fundamentales:
            </p>

            <div className="grid gap-3 mt-3">
              <div className="bg-white border-l-4 border-blue-500 pl-4 py-2 rounded-r">
                <h4 className="font-bold text-gray-800 mb-1 text-sm">1. Pertinencia</h4>
                <p className="text-gray-600 text-xs">
                  El documento debe estar orientado al público objetivo, responder a sus necesidades y contener
                  la información suficiente sin contenido superfluo.
                </p>
              </div>

              <div className="bg-white border-l-4 border-green-500 pl-4 py-2 rounded-r">
                <h4 className="font-bold text-gray-800 mb-1 text-sm">2. Facilidad de Búsqueda</h4>
                <p className="text-gray-600 text-xs">
                  El documento debe tener una estructura clara con títulos descriptivos, uso de listas y viñetas,
                  e incorporación de elementos visuales que faciliten la navegación.
                </p>
              </div>

              <div className="bg-white border-l-4 border-purple-500 pl-4 py-2 rounded-r">
                <h4 className="font-bold text-gray-800 mb-1 text-sm">3. Comprensibilidad</h4>
                <p className="text-gray-600 text-xs">
                  El texto debe ser fácil de entender, utilizando vocabulario apropiado al nivel B2 del MCERL,
                  oraciones claras, voz activa, y evitando negaciones múltiples y expresiones indeterminadas.
                </p>
              </div>

              <div className="bg-white border-l-4 border-orange-500 pl-4 py-2 rounded-r">
                <h4 className="font-bold text-gray-800 mb-1 text-sm">4. Usabilidad</h4>
                <p className="text-gray-600 text-xs">
                  El documento debe estar validado con usuarios reales y orientar a la acción, indicando
                  claramente los siguientes pasos que debe tomar el lector.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Qué mide la aplicación */}
      <Card>
        <CardHeader>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            <FontAwesomeIcon icon={faChartLine} className="text-green-600" />
            ¿Qué mide esta aplicación?
          </h2>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-gray-700 leading-relaxed">
            La herramienta realiza un análisis exhaustivo de tus textos evaluando múltiples aspectos:
          </p>

          <div className="space-y-6 mt-4">
            {/* Legibilidad */}
            <div>
              <h3 className="font-bold text-lg text-gray-800 mb-3 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 text-sm">1</span>
                Métricas de Legibilidad
              </h3>
              <ul className="space-y-2 ml-10 text-gray-600">
                <li className="flex items-start gap-2">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mt-1" />
                  <span><strong>Índice Flesch-Szigriszt:</strong> Adaptación del índice Flesch para el español (objetivo: 55-65 para nivel B2)</span>
                </li>
                <li className="flex items-start gap-2">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mt-1" />
                  <span><strong>Índice INFLESZ:</strong> Versión mejorada del índice anterior para textos españoles</span>
                </li>
                <li className="flex items-start gap-2">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mt-1" />
                  <span><strong>Promedio de palabras por oración:</strong> Evalúa la complejidad estructural (objetivo: 15-20 palabras)</span>
                </li>
                <li className="flex items-start gap-2">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mt-1" />
                  <span><strong>Promedio de sílabas por palabra:</strong> Mide la complejidad léxica (objetivo: ≤2.5 sílabas)</span>
                </li>
              </ul>
            </div>

            {/* Vocabulario */}
            <div>
              <h3 className="font-bold text-lg text-gray-800 mb-3 flex items-center gap-2">
                <span className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 text-sm">2</span>
                Análisis de Vocabulario
              </h3>
              <ul className="space-y-2 ml-10 text-gray-600">
                <li className="flex items-start gap-2">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mt-1" />
                  <span><strong>Distribución por niveles MCERL:</strong> Clasifica el vocabulario en niveles A1-C2</span>
                </li>
                <li className="flex items-start gap-2">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mt-1" />
                  <span><strong>Términos que requieren glosario:</strong> Identifica palabras C1-C2 especializadas que necesitan definición</span>
                </li>
                <li className="flex items-start gap-2">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="text-orange-500 mt-1" />
                  <span><strong>Vocabulario C1-C2 para simplificar:</strong> Palabras avanzadas que deberían reemplazarse por alternativas más sencillas</span>
                </li>
                <li className="flex items-start gap-2">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mt-1" />
                  <span><strong>Palabras complejas (análisis morfológico):</strong> Detecta palabras complejas por su estructura y longitud</span>
                </li>
              </ul>
            </div>

            {/* Estructura */}
            <div>
              <h3 className="font-bold text-lg text-gray-800 mb-3 flex items-center gap-2">
                <span className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600 text-sm">3</span>
                Estructura y Formato
              </h3>
              <ul className="space-y-2 ml-10 text-gray-600">
                <li className="flex items-start gap-2">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mt-1" />
                  <span><strong>Uso de mayúsculas:</strong> Detecta palabras completamente en mayúsculas que dificultan la lectura</span>
                </li>
                <li className="flex items-start gap-2">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mt-1" />
                  <span><strong>Voz activa vs pasiva:</strong> Identifica el uso de voz pasiva que complica la comprensión</span>
                </li>
                <li className="flex items-start gap-2">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mt-1" />
                  <span><strong>Oraciones subordinadas:</strong> Detecta estructuras sintácticas complejas</span>
                </li>
                <li className="flex items-start gap-2">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mt-1" />
                  <span><strong>Negaciones múltiples:</strong> Identifica dobles negaciones que dificultan la comprensión</span>
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Categorías de Palabras */}
      <Card>
        <CardHeader>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            <FontAwesomeIcon icon={faLanguage} className="text-orange-600" />
            Categorías de Palabras
          </h2>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-gray-700 leading-relaxed">
            El sistema clasifica las palabras en tres categorías según su prioridad de atención:
          </p>

          <div className="space-y-4 mt-4">
            <div className="border-2 border-orange-200 rounded-lg p-4 bg-orange-50">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-3 py-1 bg-orange-600 text-white text-xs font-bold rounded-full">REQUERIDO</span>
                <h3 className="font-bold text-gray-800">Términos que Requieren Glosario</h3>
              </div>
              <p className="text-gray-600 text-sm">
                Palabras técnicas o especializadas de nivel C1-C2 que aparecen en la base de datos de glosario.
                <strong> Deben incluirse en un glosario</strong> con sus definiciones para que el texto sea comprensible.
              </p>
            </div>

            <div className="border-2 border-red-200 rounded-lg p-4 bg-red-50">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-3 py-1 bg-red-600 text-white text-xs font-bold rounded-full">PRIORIDAD ALTA</span>
                <h3 className="font-bold text-gray-800">Vocabulario C1-C2 para Simplificar</h3>
              </div>
              <p className="text-gray-600 text-sm">
                Palabras de nivel C1-C2 del corpus que NO están en el glosario.
                <strong> Deberían ser simplificadas o reemplazadas</strong> por alternativas más sencillas de niveles inferiores.
              </p>
            </div>

            <div className="border-2 border-amber-200 rounded-lg p-4 bg-amber-50">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-3 py-1 bg-amber-600 text-white text-xs font-bold rounded-full">PRIORIDAD MEDIA</span>
                <h3 className="font-bold text-gray-800">Palabras Complejas (Análisis Morfológico)</h3>
              </div>
              <p className="text-gray-600 text-sm">
                Palabras detectadas como complejas por su estructura morfológica, longitud o número de sílabas.
                <strong> Se recomienda revisar</strong> y simplificar cuando sea posible.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cómo utilizar la herramienta */}
      <Card>
        <CardHeader>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            <FontAwesomeIcon icon={faFileLines} className="text-teal-600" />
            Cómo utilizar la herramienta
          </h2>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h3 className="font-bold text-lg text-gray-800 mb-3">1. Tipos de Análisis</h3>
            <ul className="space-y-2 ml-4 text-gray-600">
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">•</span>
                <span><strong>Análisis de Texto:</strong> Copia y pega directamente el texto que deseas analizar</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">•</span>
                <span><strong>Análisis de Documentos:</strong> Sube archivos DOCX o PDF para analizar su contenido</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">•</span>
                <span><strong>Análisis Web:</strong> Introduce una URL para analizar el contenido de una página web</span>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-bold text-lg text-gray-800 mb-3">2. Selección de Sectores</h3>
            <p className="text-gray-600 ml-4">
              Selecciona los sectores temáticos relevantes para tu texto (Salud, Legal, Educación, etc.).
              Esto permite al sistema utilizar el vocabulario especializado apropiado para identificar términos técnicos.
            </p>
          </div>

          <div>
            <h3 className="font-bold text-lg text-gray-800 mb-3">3. Interpretar Resultados</h3>
            <ul className="space-y-2 ml-4 text-gray-600">
              <li className="flex items-start gap-2">
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mt-1" />
                <span><strong>Verde (Pasa):</strong> El criterio cumple con los estándares de Lenguaje Claro</span>
              </li>
              <li className="flex items-start gap-2">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-orange-500 mt-1" />
                <span><strong>Naranja (Advertencia):</strong> El criterio está en el límite, se recomienda revisar</span>
              </li>
              <li className="flex items-start gap-2">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 mt-1" />
                <span><strong>Rojo (No pasa):</strong> El criterio no cumple, requiere corrección</span>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-bold text-lg text-gray-800 mb-3">4. Validación Manual</h3>
            <p className="text-gray-600 ml-4">
              Algunos criterios (como Pertinencia y Usabilidad) requieren validación manual.
              Marca cada criterio como "Cumple" o "No cumple" según tu evaluación del documento.
            </p>
          </div>

          <div>
            <h3 className="font-bold text-lg text-gray-800 mb-3">5. Gestión de Corpus y Glosarios</h3>
            <ul className="space-y-2 ml-4 text-gray-600">
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">•</span>
                <span><strong>Corpus Léxico:</strong> Base de datos de palabras clasificadas por nivel MCERL y sector</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">•</span>
                <span><strong>Glosarios:</strong> Términos especializados que requieren definición en los documentos</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-600 font-bold">•</span>
                <span>Puedes filtrar por nivel MCERL y sector para gestionar mejor el vocabulario</span>
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Fuentes de Referencia y Metodología */}
      <Card>
        <CardHeader>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            <FontAwesomeIcon icon={faGraduationCap} className="text-blue-600" />
            Fuentes de Referencia y Metodología
          </h2>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
            <p className="text-gray-700 leading-relaxed">
              Esta herramienta ha sido desarrollada mediante un exhaustivo trabajo de investigación lingüística y computacional,
              fundamentado en fuentes oficiales de referencia del español y el Marco Común Europeo de Referencia para las Lenguas (MCERL).
            </p>
          </div>

          {/* Trabajo de Investigación */}
          <div>
            <h3 className="font-bold text-lg text-gray-800 mb-4 flex items-center gap-2">
              <FontAwesomeIcon icon={faFlask} className="text-purple-600" />
              Trabajo de Investigación y Desarrollo
            </h3>
            <div className="space-y-4 ml-6">
              <div>
                <h4 className="font-semibold text-gray-800 mb-2">Construcción del Corpus Léxico</h4>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Nuestro corpus léxico ha sido construido a partir de un riguroso proceso de análisis, categorización y clasificación
                  de más de 28,000 palabras del español. Este trabajo implica:
                </p>
                <ul className="mt-2 ml-4 space-y-1 text-gray-600 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold mt-1">•</span>
                    <span><strong>Investigación y recopilación:</strong> Análisis de múltiples fuentes oficiales y académicas del español</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold mt-1">•</span>
                    <span><strong>Categorización por niveles MCERL:</strong> Clasificación individualizada de cada término según su complejidad lingüística</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold mt-1">•</span>
                    <span><strong>Análisis morfológico:</strong> Estudio de la estructura, formación y características de cada palabra</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold mt-1">•</span>
                    <span><strong>Ponderación y validación:</strong> Aplicación de criterios propios basados en frecuencia de uso, contexto y complejidad</span>
                  </li>
                </ul>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">Sistema de Clasificación Inteligente</h4>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  Hemos desarrollado un sistema algorítmico de clasificación automática para palabras no incluidas en el corpus base,
                  basado en fórmulas lingüísticas y computacionales propias:
                </p>
                <ul className="ml-4 space-y-2 text-gray-600 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 font-bold mt-1">•</span>
                    <div>
                      <strong>Análisis silábico:</strong>
                      <code className="block mt-1 bg-white px-2 py-1 rounded text-xs font-mono">
                        ComplexityScore = (syllableCount × 0.4) + (wordLength × 0.3) + (characterComplexity × 0.3)
                      </code>
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 font-bold mt-1">•</span>
                    <div>
                      <strong>Ponderación por frecuencia:</strong> Integración de datos de frecuencia de uso en distintos niveles educativos
                      <code className="block mt-1 bg-white px-2 py-1 rounded text-xs font-mono">
                        Level = determineLevel(Σ(freq_A1...B2), Σ(freq_C1...C2))
                      </code>
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 font-bold mt-1">•</span>
                    <span><strong>Detección de variaciones morfológicas:</strong> Reconocimiento automático de derivaciones, compuestos y flexiones</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 font-bold mt-1">•</span>
                    <span><strong>Normalización ortográfica:</strong> Eliminación de diacríticos para análisis comparativo sin perder información léxica</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Fuentes Oficiales */}
          <div>
            <h3 className="font-bold text-lg text-gray-800 mb-4">Fuentes Oficiales de Referencia</h3>
            <p className="text-gray-600 text-sm mb-4 leading-relaxed">
              Nuestro trabajo se fundamenta en el análisis y estudio de las siguientes fuentes académicas y oficiales,
              sobre las cuales hemos aplicado nuestra propia metodología de clasificación y ponderación:
            </p>

            <div className="space-y-4">
              {/* RAE */}
              <div className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-800 mb-1">Real Academia Española (RAE) - CORPES XXI</h4>
                    <p className="text-gray-600 text-xs mb-2">
                      Corpus de Referencia del Español Actual del siglo XXI. Utilizado para el análisis de frecuencias
                      y patrones de uso del español contemporáneo en diferentes registros y variedades geográficas.
                    </p>
                    <a
                      href="https://www.rae.es/corpes/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 text-xs font-medium inline-flex items-center gap-1"
                    >
                      Visitar CORPES XXI
                      <FontAwesomeIcon icon={faExternalLinkAlt} className="text-xs" />
                    </a>
                  </div>
                </div>
              </div>

              {/* Cervantes */}
              <div className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-800 mb-1">Instituto Cervantes - Plan Curricular</h4>
                    <p className="text-gray-600 text-xs mb-2">
                      Plan Curricular del Instituto Cervantes: Niveles de referencia para el español. Fuente oficial
                      para la clasificación del vocabulario según los niveles MCERL (A1-C2) específicos para el español.
                    </p>
                    <a
                      href="https://cvc.cervantes.es/ensenanza/biblioteca_ele/plan_curricular/default.htm"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 text-xs font-medium inline-flex items-center gap-1"
                    >
                      Visitar Plan Curricular
                      <FontAwesomeIcon icon={faExternalLinkAlt} className="text-xs" />
                    </a>
                  </div>
                </div>
              </div>

              {/* ELELex */}
              <div className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-800 mb-1">ELELex - CENTAL (UCLouvain)</h4>
                    <p className="text-gray-600 text-xs mb-2">
                      Proyecto de investigación académica sobre la clasificación de vocabulario del español por niveles CEFR/MCERL.
                      Base de datos léxica resultado de trabajo investigativo universitario con análisis de frecuencias por nivel educativo.
                    </p>
                    <a
                      href="https://cental.uclouvain.be/cefrlex/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 text-xs font-medium inline-flex items-center gap-1"
                    >
                      Visitar ELELex Project
                      <FontAwesomeIcon icon={faExternalLinkAlt} className="text-xs" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Licencia */}
          <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
              <FontAwesomeIcon icon={faBalanceScale} className="text-green-600" />
              Metodología y Atribución
            </h4>
            <p className="text-gray-700 text-sm leading-relaxed">
              El <strong>corpus léxico</strong> que fundamenta el análisis de vocabulario ha sido construido mediante trabajo original de investigación,
              análisis y clasificación. Las fuentes oficiales mencionadas (RAE, Instituto Cervantes, ELELex) han sido utilizadas como referencias académicas
              para fundamentar el trabajo de investigación lingüística, sobre las cuales se ha aplicado un proceso exhaustivo de
              análisis, categorización, ponderación y validación para construir un corpus único y específico para el análisis
              de legibilidad y comprensibilidad del español según los criterios de Lenguaje Claro y la norma UNE-ISO 24495-1:2024.
            </p>
          </div>

          {/* Descarga del Corpus */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-indigo-200 rounded-lg p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-indigo-600 rounded-lg flex items-center justify-center">
                  <FontAwesomeIcon icon={faDownload} className="text-white text-xl" />
                </div>
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-gray-800 mb-2 text-lg">Descarga del Corpus Léxico</h4>
                <p className="text-gray-700 text-sm leading-relaxed mb-3">
                  El corpus léxico completo utilizado por esta herramienta está disponible para <strong>descarga gratuita</strong> en formato JSON
                  para uso no comercial en investigación, educación y proyectos académicos.
                </p>
                <div className="bg-white rounded-lg p-4 mb-4 border border-indigo-100">
                  <h5 className="font-semibold text-gray-800 text-sm mb-2">Contenido del corpus:</h5>
                  <ul className="space-y-1 text-xs text-gray-600">
                    <li className="flex items-center gap-2">
                      <span className="text-indigo-600">•</span>
                      <span><strong>28,000+ palabras</strong> clasificadas por nivel MCERL (A1-C2)</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-indigo-600">•</span>
                      <span>Información morfológica: sílabas, lemas, raíces</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-indigo-600">•</span>
                      <span>Puntuaciones de complejidad y frecuencia</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-indigo-600">•</span>
                      <span>Categorías gramaticales y normalización ortográfica</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-indigo-600">•</span>
                      <span>Metadatos con atribución completa de fuentes</span>
                    </li>
                  </ul>
                </div>
                <button
                  onClick={handleDownloadCorpus}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-3 shadow-md hover:shadow-lg"
                >
                  <FontAwesomeIcon icon={faDownload} />
                  <span>Descargar Corpus (JSON)</span>
                </button>
                <div className="mt-4 pt-4 border-t border-indigo-200">
                  <p className="text-xs text-gray-600 leading-relaxed mb-2">
                    <strong>Licencia:</strong> Creative Commons Atribución-NoComercial-CompartirIgual 4.0 Internacional (CC BY-NC-SA 4.0)
                  </p>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    Este corpus puede ser utilizado libremente para fines no comerciales. Si lo utilizas en investigación o publicaciones,
                    por favor cita las fuentes originales (RAE, Instituto Cervantes, ELELex) y esta herramienta.
                  </p>
                  <a
                    href="https://creativecommons.org/licenses/by-nc-sa/4.0/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:text-indigo-800 text-xs font-medium inline-flex items-center gap-1 mt-2"
                  >
                    Ver términos de la licencia CC BY-NC-SA 4.0
                    <FontAwesomeIcon icon={faExternalLinkAlt} className="text-xs" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Consejos */}
      <Card>
        <CardHeader>
          <h2 className="text-2xl font-bold text-gray-800">Consejos para mejorar tus textos</h2>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3 text-gray-600">
            <li className="flex items-start gap-3">
              <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mt-1 flex-shrink-0" />
              <span><strong>Utiliza oraciones cortas:</strong> Intenta que tengan entre 15-20 palabras</span>
            </li>
            <li className="flex items-start gap-3">
              <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mt-1 flex-shrink-0" />
              <span><strong>Prefiere la voz activa:</strong> "El médico receta el medicamento" en lugar de "El medicamento es recetado por el médico"</span>
            </li>
            <li className="flex items-start gap-3">
              <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mt-1 flex-shrink-0" />
              <span><strong>Evita dobles negaciones:</strong> "Nadie puede no cumplir" → "Todos deben cumplir"</span>
            </li>
            <li className="flex items-start gap-3">
              <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mt-1 flex-shrink-0" />
              <span><strong>Sustituye palabras complejas:</strong> "Procedimiento" → "Proceso", "Adquisición" → "Compra"</span>
            </li>
            <li className="flex items-start gap-3">
              <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mt-1 flex-shrink-0" />
              <span><strong>Incluye un glosario:</strong> Define los términos técnicos inevitables de forma clara</span>
            </li>
            <li className="flex items-start gap-3">
              <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mt-1 flex-shrink-0" />
              <span><strong>Usa listas y viñetas:</strong> Facilitan la lectura y la búsqueda de información</span>
            </li>
            <li className="flex items-start gap-3">
              <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mt-1 flex-shrink-0" />
              <span><strong>Evita mayúsculas sostenidas:</strong> Solo usa mayúsculas para iniciales y acrónimos necesarios</span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
